import { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "react-auth-kit";
import { NAVIGATION_ROUTES } from "./utils/NavigationUtils";
import { SIDER_TABS } from "./utils/Constants";
import {
  AGENT_AUDIT_LOG_ROUTE_PERMISSIONS,
  MAINTENANCE_MODE_ROUTE_PERMISSIONS,
  DUPLICATE_IDENTITY_REVIEW_ROUTE_PERMISSIONS,
  WITHDRAWAL_REVIEW_ROUTE_PERMISSIONS,
} from "./utils/constants/permissionsConstants";
import { TRIVELTA_PRODUCTS } from "./utils/products";
import { protectedElement } from "./routing/protectedElement";

// component imports
import LogIn from "./components/LogIn/LogIn";
import RedirectIfAuthenticated from "./components/LogIn/RedirectIfAuthenticated";
import AllUsers from "./components/Users/AllUsers/AllUsers";
import UserManager from "./components/Users/UserManager/UserManager";
import KPIs from "./components/KPIs/KPIs";
import ReportingSummary from "./components/ReportingSummary/ReportingSummary";
import ReportingSummaryProfitability from "./components/ReportingSummary/ReportingSummaryProfitability";
import SBTopUsers from "./components/Sportsbook/TopUsers/TopUsers";
import CasinoTopUsers from "./components/Casino/TopUsers/TopUsers";
import BonusConfigs from "./components/Promotions/BonusConfigs/BonusConfigs";
import WithdrawalReview from "./components/Transactions/WithdrawalReviewV2/WithdrawalReview";
import FraudDetection from "./components/Transactions/FraudDetection/FraudDetection";
import EditRule from "./components/Transactions/FraudDetection/FraudDetectionRulesTable/EditRule/EditRule";
import ConfigurationPage from "./components/Compliance/Configuration/ConfigurationPage";
import MaintenanceMode from "./components/Compliance/MaintenanceMode/MaintenanceMode";
import DuplicateIdentityReview from "./components/Compliance/DuplicateIdentityReview/DuplicateIdentityReview";
import PredictionsReports from "./components/Predictions/Reports/Reports";
import PredictionsConfiguration from "./components/Predictions/Configuration/Configuration";
import ProfileConfigurationPage from "./components/Configurations/profile-configuration/ProfileConfigurationPage";
import StateConfigurationPage from "./components/Configurations/StateConfiguration/StateConfigurationPage";

import "./App.css";
import "./fonts.css";
import "./table.css";
import "@fontsource/raleway/800-italic.css";
import "@fontsource/sora/400.css";
import "@fontsource/sora/600.css"; // 600 is the weight for Semibold
import Payments from "./components/Payments/Payments";
import PaymentReporting from "./components/Payments/PaymentReporting/PaymentReporting";
import PaymentsLimits from "./components/Payments/Limits/Limits";
import TopUsers from "./components/TopUsers/TopUsers";
import Reports from "./components/Reports/Reports";
import FieldbookEventsList from "./components/Sportsbook/FieldbookEventsList";
import MonitoringLatestBetsList from "./components/Sportsbook/MonitoringLatestBetsList";
import ConfigurationBetLimits from "./components/Sportsbook/ConfigurationBetLimits";
import ConfigurationAccountConfig from "./components/Sportsbook/ConfigurationAccountConfig";
import EarlyPayout from "./components/Sportsbook/EarlyPayout/EarlyPayout";
import CasinoFinanceReports from "./components/Casino/CasinoFinanceReports/CasinoFinanceReports";
import CasinoProvidersReports from "./components/Casino/CasinoProvidersReports/CasinoProvidersReports";
import CasinoGamesReports from "./components/Casino/CasinoGamesReports/CasinoGamesReports";
import CasinoScheduledTasks from "./components/Configurations/ScheduledTasks/ScheduledTasks";
import PromotionsManagement from "./components/Promotions/PromotionsManagement/PromotionsManagement";
import CampaignDetails from "./components/Promotions/PromotionsManagement/CampaignDetails/CampaignDetails";
import NewPromotion from "./components/Promotions/NewPromotion/NewPromotion";
import Affiliates from "./components/Affiliates/Affiliates";
import AffiliateProfile from "./components/Affiliates/AffiliateProfile/AffiliateProfile";
import NewAffiliate from "./components/Affiliates/NewAffiliate/NewAffiliate";
import UtmReports from "./components/Promotions/UtmReports/UtmReports";
import AffiliatePortalLayout from "./components/AffiliatePortal/AffiliatePortalLayout";
import AffiliatePortalDashboard from "./components/AffiliatePortal/Dashboard/AffiliatePortalDashboard";
import RequireAffiliateAuth from "./components/AffiliatePortal/RequireAffiliateAuth";
import {
  AFFILIATE_PORTAL_DASHBOARD_ROUTE,
  AFFILIATE_PORTAL_PATH_PREFIX,
  AFFILIATE_PORTAL_SET_PASSWORD_ROUTE,
  AFFILIATE_PORTAL_SIGN_IN_ROUTE,
  isAffiliatePortalPath,
} from "./routing/affiliatePortalRoutes";
import RegulatorPortalLayout from "./components/RegulatorPortal/RegulatorPortalLayout";
import RegulatorPortalDashboard from "./components/RegulatorPortal/Dashboard/RegulatorPortalDashboard";
import RegulatorPortalNotFound from "./components/RegulatorPortal/RegulatorPortalNotFound";
import RequireRegulatorAuth from "./components/RegulatorPortal/RequireRegulatorAuth";
import RegulatorPortalSignIn from "./components/RegulatorPortal/RegulatorPortalSignIn";
import RegulatorPortalSetPassword from "./components/RegulatorPortal/RegulatorPortalSetPassword";
import RegulatorPortalResults from "./components/RegulatorPortal/Compliance/RegulatorPortalResults";
import RegulatorPortalResultDetail from "./components/RegulatorPortal/Compliance/RegulatorPortalResultDetail";
import RegulatorPortalRules from "./components/RegulatorPortal/Compliance/RegulatorPortalRules";
import RegulatorPortalRuleDetail from "./components/RegulatorPortal/Compliance/RegulatorPortalRuleDetail";
import RegulatorPortalServices from "./components/RegulatorPortal/Compliance/RegulatorPortalServices";
import RegulatorPortalReports from "./components/RegulatorPortal/Compliance/RegulatorPortalReports";
import RegulatorPortalReportDetail from "./components/RegulatorPortal/Compliance/RegulatorPortalReportDetail";
import {
  REGULATOR_PORTAL_PATH_PREFIX,
  REGULATOR_PORTAL_SET_PASSWORD_ROUTE,
  REGULATOR_PORTAL_SIGN_IN_ROUTE,
} from "./components/RegulatorPortal/regulatorPortalRoutes";
import { staffRegulatorsAppRoute } from "./components/StaffRegulators/staffRegulatorsAppRoute";
import Wallet from "./components/Transactions/Wallet/Wallet";
import TransactionsTable from "./components/Transactions/TransactionsTable/TransactionsTable";
import Routing from "./components/Transactions/Routing/Routing";
import EventDetails from "./components/Sportsbook/EventDetails/EventDetails";
import BetsTypesList from "./components/Sportsbook/EventDetails/EventKpi/BetsTypesList/BetsTypesList";
import SGPRisk from "./components/Sportsbook/SGPRisk/SGPRisk";
import SGPIdBetsList from "./components/Sportsbook/SGPRisk/SGPIdBetsList/SGPIdBetsList";
import SGPLimits from "./components/Sportsbook/SGPLimits/SGPLimits";
import { SGP_RISK_BETS_LIST_ROUTE } from "./components/Sportsbook/SGPRisk/sgpRiskNavigation";
import { setAppConfig } from "./store/slice/configSlice";
import FeatureFlagService from "./services/FeatureFlagService";
import { useDispatch } from "react-redux";
import { applyResolvedI18nLanguage } from "./utils/i18n/languagePreference";
import { mergeV2Localization } from "./utils/i18n/platformConfig";
import RebetLoader from "./components/CommonComponents/RebetLoader/RebetLoader";
import PublicAdminPanelInstance from "./api/PublicAdminPanelInstance";
import { API_ROUTES } from "./api/ApiRoutes";
import SportsbookFM from "./components/Configurations/SportsbookFM/SportsbookFM";
import GamesConfigurationPage from "./components/Configurations/casino-configuration/games/games";
import ProvidersConfigurationPage from "./components/Configurations/casino-configuration/providers/providers";
import CollectionsConfigurationPage from "./components/Configurations/casino-configuration/collections/collections";
import ViewsConfigurationPage from "./components/Configurations/casino-configuration/views/Views";
import PagesConfigurationPage from "./components/Configurations/casino-configuration/pages/Pages";
import TagsConfigurationPage from "./components/Configurations/casino-configuration/tags/Tags";
import FeaturesConfigurationPage from "./components/Configurations/casino-configuration/features/Features";
import CasinoPreviewLazyRoute from "./components/Configurations/casino-configuration/preview/CasinoPreviewLazyRoute";
import PokerConfigurationPage from "./components/Configurations/poker-configuration/PokerConfigurationPage";
import { POKER_CONFIGURATION_PAGES } from "./utils/constants/poker/pokerConfigurationData";
import SeasonsList from "./components/GamePass/SeasonsList/SeasonsList";
import CreateSeason from "./components/GamePass/CreateSeason/CreateSeason";
import SeasonDetails from "./components/GamePass/SeasonDetails/SeasonDetails";
import MonthlyChallenge from "./components/GamePass/Monthly/MonthlyChallenge";
import MonthlyConfiguration from "./components/GamePass/Monthly/Configuration/MonthlyConfiguration";
import MonthlyDetails from "./components/GamePass/Monthly/Details/MonthlyDetails";
import GamePassStyle from "./components/GamePass/Style/GamePassStyle";
import SetPassword from "./components/SetPassword/SetPassword";
import ResetPassword from "./components/ResetPassword/ResetPassword";
import StaffManagement from "./components/StaffManagement/StaffManagement";
import ManualActions from "./components/ManualActions/ManualActions";
import useAppUpdateNotifier from "./hooks/useAppUpdateNotifier";
import TemplatesPage from "./components/Promotions/DynamicBonuses/Templates/TemplatesPage";
import CreateTemplatePage from "./components/Promotions/DynamicBonuses/Templates/CreateTemplatePage";
import EditTemplatePage from "./components/Promotions/DynamicBonuses/Templates/EditTemplatePage";
import SegmentsPage from "./components/Promotions/DynamicBonuses/Segments/SegmentsPage";
import CreateSegmentPage from "./components/Promotions/DynamicBonuses/Segments/CreateSegmentPage";
import SegmentDetailPage from "./components/Promotions/DynamicBonuses/Segments/SegmentDetailPage";
import CampaignsPage from "./components/Promotions/DynamicBonuses/Campaigns/CampaignsPage";
import CreateCampaignPage from "./components/Promotions/DynamicBonuses/Campaigns/CreateCampaignPage";
import CreateAffiliateCampaignPage from "./components/Promotions/DynamicBonuses/Campaigns/CreateAffiliateCampaignPage";
import EditCampaignPage from "./components/Promotions/DynamicBonuses/Campaigns/EditCampaignPage";
import SeriesDetailPage from "./components/Promotions/DynamicBonuses/Series/SeriesDetailPage";
import EditSeriesPage from "./components/Promotions/DynamicBonuses/Series/EditSeriesPage";
import { useIpWhitelistAccess } from "./components/IpWhitelistGate/IpWhitelistGate";
import { isIpWhitelistBlockedError } from "./services/ipWhitelistAccessService";
import { hasValidAffiliatePortalSession } from "./utils/tokenUtils";
import { AUTH_AUDIENCES } from "./utils/auth/authAudience";
import RequireAuth from "./RequireAuth";
import Layout from "./components/CommonComponents/Layout/Layout";

const LegacyRouteRedirect = ({ fromPathPrefix, toPathPrefix }) => {
  const location = useLocation();
  const nextPathname = location.pathname.startsWith(fromPathPrefix)
    ? location.pathname.replace(fromPathPrefix, toPathPrefix)
    : toPathPrefix;
  return (
    <Navigate
      to={`${nextPathname}${location.search}${location.hash}`}
      replace
    />
  );
};

const App = () => {
  const dispatch = useDispatch();
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const { blockAccess } = useIpWhitelistAccess();
  useAppUpdateNotifier();

  useEffect(() => {
    const fetchAppConfig = async () => {
      // Neither portal consumes platform config, so skip the call on their
      // routes.
      if (
        isAffiliatePortalPath(window.location.pathname) ||
        window.location.pathname.startsWith(REGULATOR_PORTAL_PATH_PREFIX)
      ) {
        return;
      }
      setIsLoadingConfig(true);
      try {
        // Prime both caches up-front — otherwise <FeatureFlagGuard> would
        // lazy-fetch v2 on first mount. v2 catches its own error; v1 falls
        // through to the outer catch.
        const [appConfigResponse, productsConfigResponse] = await Promise.all([
          PublicAdminPanelInstance?.get(API_ROUTES?.GET_PLATFORM_CONFIG),
          PublicAdminPanelInstance?.get(API_ROUTES?.GET_PLATFORM_CONFIG_V2).catch(
            (err) => {
              if (isIpWhitelistBlockedError(err)) throw err;
              console.error("Error fetching Trivelta Products v2:", err);
              return null;
            },
          ),
        ]);
        const platformConfigV2 = productsConfigResponse?.data?.data;
        const appConfigData = mergeV2Localization(
          appConfigResponse?.data?.data,
          platformConfigV2,
        );
        dispatch(setAppConfig(appConfigData));
        FeatureFlagService.hydrateFromPlatformConfig(
          appConfigData?.features_status_config,
        );
        FeatureFlagService.hydrateProductsFromV2(
          platformConfigV2?.products,
        );
        // Resolution-module apply: admin/guest language preference overrides
        // platform_language when present.
        await applyResolvedI18nLanguage(appConfigData);
      } catch (error) {
        if (isIpWhitelistBlockedError(error)) {
          blockAccess();
          return;
        }
        // Config is optional for initial render; error handled by empty state.
      }
      setIsLoadingConfig(false);
    };
    fetchAppConfig();
  }, [blockAccess, dispatch]);

  return (
    <div className="app-container">
      {isLoadingConfig ? (
        <div className="loading-container">
          <RebetLoader height={"32px"} width={"32px"} />
        </div>
      ) : (
        <AuthProvider
          authType={"cookie"}
          authName={"_auth"}
          cookieDomain={window.location.hostname}
          cookieSecure={false}
        >
          <>
            {/* Each route is given separation to allow for visual consistency and protected routes */}
            <Routes>
              {/* Unprotected routes */}
                <Route
                  path={NAVIGATION_ROUTES?.LOGIN}
                  element={
                    <RedirectIfAuthenticated>
                      <LogIn audience={AUTH_AUDIENCES.STAFF} />
                    </RedirectIfAuthenticated>
                  }
                />

              {/*
                Affiliate Portal — external surface for affiliate
                partners. Lives outside the operator auth wrapper, uses its
                own sign-in endpoint and RequireAffiliateAuth group check, and
                remains outside the Staff IP-whitelist gate.
              */}
              <Route
                path={AFFILIATE_PORTAL_SIGN_IN_ROUTE}
                element={
                  hasValidAffiliatePortalSession() ? (
                    <Navigate
                      to={AFFILIATE_PORTAL_DASHBOARD_ROUTE}
                      replace
                    />
                  ) : (
                    <LogIn audience={AUTH_AUDIENCES.AFFILIATE} />
                  )
                }
              />
              <Route
                path={AFFILIATE_PORTAL_SET_PASSWORD_ROUTE}
                element={<SetPassword audience={AUTH_AUDIENCES.AFFILIATE} />}
              />
              <Route
                path={AFFILIATE_PORTAL_PATH_PREFIX}
                element={
                  <RequireAffiliateAuth>
                    <AffiliatePortalLayout />
                  </RequireAffiliateAuth>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route
                  path="dashboard"
                  element={<AffiliatePortalDashboard />}
                />
                <Route
                  path="*"
                  element={
                    <Navigate
                      to={AFFILIATE_PORTAL_DASHBOARD_ROUTE}
                      replace
                    />
                  }
                />
              </Route>

              {/*
                Regulator Portal — second external surface, outside the
                operator auth wrapper. Its dedicated sign-in keeps the
                IP-whitelist exemption isolated from the shared staff login.
                The guard validates the authenticated /me response and owns
                the loading, pending and denied outcomes.
              */}
              <Route
                path={REGULATOR_PORTAL_SIGN_IN_ROUTE}
                element={<RegulatorPortalSignIn />}
              />
              <Route
                path={REGULATOR_PORTAL_SET_PASSWORD_ROUTE}
                element={<RegulatorPortalSetPassword />}
              />
              <Route
                path={REGULATOR_PORTAL_PATH_PREFIX}
                element={
                  <RequireRegulatorAuth>
                    <RegulatorPortalLayout />
                  </RequireRegulatorAuth>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route
                  path="dashboard"
                  element={<RegulatorPortalDashboard />}
                />
                <Route
                  path="results"
                  element={<RegulatorPortalResults />}
                />
                <Route
                  path="results/:resultId"
                  element={<RegulatorPortalResultDetail />}
                />
                <Route path="rules" element={<RegulatorPortalRules />} />
                <Route
                  path="rules/:ruleId"
                  element={<RegulatorPortalRuleDetail />}
                />
                <Route
                  path="services"
                  element={<RegulatorPortalServices />}
                />
                <Route path="reports" element={<RegulatorPortalReports />} />
                <Route
                  path="reports/:runId"
                  element={<RegulatorPortalReportDetail />}
                />
                <Route path="*" element={<RegulatorPortalNotFound />} />
              </Route>

              <Route
                path={NAVIGATION_ROUTES?.SET_PASSWORD}
                element={<SetPassword />}
              />
              <Route
                path={NAVIGATION_ROUTES?.RESET_PASSWORD}
                element={<ResetPassword />}
              />
              <Route
                path="/sportsbook/configuration/bet-limits"
                element={
                  <LegacyRouteRedirect
                    fromPathPrefix="/sportsbook/configuration/bet-limits"
                    toPathPrefix={NAVIGATION_ROUTES.CONFIGURATION_BET_LIMITS}
                  />
                }
              />
              <Route
                path="/sportsbook/configurations/sportsbook-fm"
                element={
                  <LegacyRouteRedirect
                    fromPathPrefix="/sportsbook/configurations/sportsbook-fm"
                    toPathPrefix={NAVIGATION_ROUTES.SPORTSBOOK_FM}
                  />
                }
              />
              <Route
                path="/promotions/marketing-affiliates-v2/*"
                element={
                  protectedElement(
                    <LegacyRouteRedirect
                      fromPathPrefix="/promotions/marketing-affiliates-v2"
                      toPathPrefix={NAVIGATION_ROUTES.MARKETING_AFFILIATES}
                    />,
                    {
                      requiredPermission: "promotions.affiliates",
                      requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                    },
                  )
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.WITHDRAWAL_REVIEW_LEGACY_RULES}
                element={
                  <Navigate
                    to={NAVIGATION_ROUTES?.WITHDRAWAL_REVIEW}
                    replace
                  />
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.WITHDRAWAL_REVIEW_LEGACY_ADD_RULE}
                element={
                  <Navigate
                    to={NAVIGATION_ROUTES?.WITHDRAWAL_REVIEW}
                    replace
                  />
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.WITHDRAWAL_REVIEW_LEGACY_EDIT_RULE}
                element={
                  <Navigate
                    to={NAVIGATION_ROUTES?.WITHDRAWAL_REVIEW}
                    replace
                  />
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.WITHDRAWAL_REVIEW_LEGACY_V2}
                element={
                  <Navigate
                    to={NAVIGATION_ROUTES?.WITHDRAWAL_REVIEW}
                    replace
                  />
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.CONFIGURATIONS_KYC_LEGACY}
                element={
                  <Navigate
                    to={NAVIGATION_ROUTES?.CONFIGURATIONS_KYC}
                    replace
                  />
                }
              />
              <Route
                path="/sportsbook/sgp-risk/*"
                element={
                  <LegacyRouteRedirect
                    fromPathPrefix="/sportsbook/sgp-risk"
                    toPathPrefix={NAVIGATION_ROUTES.SGP_RISK}
                  />
                }
              />
              <Route
                path="/sportsbook/sgp-limits"
                element={
                  <LegacyRouteRedirect
                    fromPathPrefix="/sportsbook/sgp-limits"
                    toPathPrefix={NAVIGATION_ROUTES.SPORTSBOOK_LIMITS}
                  />
                }
              />

              <Route
                element={
                  <RequireAuth>
                    <Layout />
                  </RequireAuth>
                }
              >
                <Route
                path={NAVIGATION_ROUTES?.USERS}
                element={
                  protectedElement(<AllUsers />, {
                    requiredPermission: "users",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.USER_MANAGER}
                element={
                  protectedElement(<UserManager />, {
                    requiredPermission: "users",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.CASINO_REPORTS_PROVIDERS}
                element={
                  protectedElement(
                    <CasinoProvidersReports />,
                    {
                      requiredPermission: "casino.reports.providers",
                      requiredProduct: TRIVELTA_PRODUCTS.CASINO,
                      requiredProductFallbackFeature: "casino",
                    }
                  )
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.CASINO_REPORTS_GAMES}
                element={
                  protectedElement(
                      <CasinoGamesReports />,
                    {
                      requiredPermission: "casino.reports.games",
                      requiredProduct: TRIVELTA_PRODUCTS.CASINO,
                      requiredProductFallbackFeature: "casino",
                    }
                  )
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.CASINO_FINANCE_REPORTS}
                element={
                  protectedElement(
                    <CasinoFinanceReports />,
                    {
                      requiredPermission: "casino.reports.finance",
                      requiredProduct: TRIVELTA_PRODUCTS.CASINO,
                      requiredProductFallbackFeature: "casino",
                    }
                  )
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.FIELDBOOK_EVENTS_LIST}
                element={
                  protectedElement(<FieldbookEventsList />, {
                    requiredPermission: "sportsbook.fieldbook.events_list",
                    requiredProduct: TRIVELTA_PRODUCTS.SPORTSBOOK,
                    requiredProductFallbackFeature: "sportsbook_v2",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.MONITORING_LATEST_BETS_LIST}
                element={
                  protectedElement(<MonitoringLatestBetsList />, {
                    requiredPermission:
                      "sportsbook.monitoring.latest_bets_list",
                    requiredProduct: TRIVELTA_PRODUCTS.SPORTSBOOK,
                    requiredProductFallbackFeature: "sportsbook_v2",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.CONFIGURATION_BET_LIMITS}
                element={
                  protectedElement(<ConfigurationBetLimits />, {
                    requiredPermission: "sportsbook.configuration.bet_limits",
                    requiredProduct: TRIVELTA_PRODUCTS.SPORTSBOOK,
                    requiredProductFallbackFeature: "sportsbook_v2",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.CONFIGURATION_ACCOUNT_CONFIG}
                element={
                  protectedElement(<ConfigurationAccountConfig />, {
                    developmentMode: true,
                    requiredPermission:
                      "sportsbook.configuration.account_config",
                    requiredProduct: TRIVELTA_PRODUCTS.SPORTSBOOK,
                    requiredProductFallbackFeature: "sportsbook_v2",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.CONFIGURATION_EARLY_PAYOUT}
                element={
                  protectedElement(<EarlyPayout />, {
                    requiredPermission: "sportsbook.configuration.early_payout",
                    requiredProduct: TRIVELTA_PRODUCTS.SPORTSBOOK,
                    requiredProductFallbackFeature: "sportsbook_v2",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.KPIS}
                element={
                  protectedElement(<KPIs />, {
                    requiredPermission: [
                      "kpis",
                      "kpis.overview",
                      "kpis.financials",
                      "kpis.acquisition",
                      "kpis.sportsbook",
                      "kpis.casino",
                    ],
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.REPORTING_SUMMARY}
                element={
                  protectedElement(<ReportingSummary />, {
                    requiredPermission:
                      "sportsbook.reporting.reporting_summary",
                    requiredProduct: TRIVELTA_PRODUCTS.SPORTSBOOK,
                    requiredProductFallbackFeature: "sportsbook_v2",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.REPORTING_SUMMARY_PROFITABILITY}
                element={
                  protectedElement(<ReportingSummaryProfitability />, {
                    requiredPermission: "sportsbook.reporting.reporting_summary",
                    requiredProduct: TRIVELTA_PRODUCTS.SPORTSBOOK,
                    requiredProductFallbackFeature: "sportsbook_v2",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.AGENT_AUDIT_LOG}
                element={
                  protectedElement(<ManualActions />, {
                    requiredPermission: AGENT_AUDIT_LOG_ROUTE_PERMISSIONS,
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.STAFF_MANAGEMENT_NEW}
                element={
                  protectedElement(<StaffManagement />, {
                    requiredPermission: "staff_management.staff",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />

              <Route
                path={NAVIGATION_ROUTES?.DELAYED_PAYMENT}
                element={
                  protectedElement(
                    <Payments />,
                    {
                      requiredPermission: "payments.configuration.delayed_payment",
                      requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                    }
                  )
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.PAYMENT_REPORTING}
                element={
                  protectedElement(<PaymentReporting />, {
                    requiredPermission: "payments.reports.overview",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              {/*
                Payments → Configuration → Limits. WIP scaffolding:
                `developmentMode: true` keeps the page reachable only in
                dev/test environments until backend permissions ship.
              */}
              <Route
                path={NAVIGATION_ROUTES?.PAYMENTS_CONFIGURATION_LIMITS}
                element={
                  protectedElement(<PaymentsLimits />, {
                    requiredPermission: "payments.configuration.limits",
                    developmentMode: true,
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.SPORTSBOOK_TOP_USERS}
                element={
                  protectedElement(<SBTopUsers product={SIDER_TABS?.SPORTSBOOK} />, {
                    requiredPermission: "sportsbook.reporting.top_users",
                    requiredProduct: TRIVELTA_PRODUCTS.SPORTSBOOK,
                    requiredProductFallbackFeature: "sportsbook_v2",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.CASINO_TOP_USERS}
                element={
                  protectedElement(<CasinoTopUsers />, {
                    requiredPermission: "casino.reports.top_users",
                    requiredProduct: TRIVELTA_PRODUCTS.CASINO,
                    requiredProductFallbackFeature: "casino",
                  })
                }
              />
              {/*
                Poker → Reports / Configuration. WIP scaffolding:
                `developmentMode: true` keeps the pages reachable only in
                dev/test environments until Poker is ready for GA.
              */}
              <Route
                path={NAVIGATION_ROUTES?.POKER_TOP_USERS}
                element={
                  protectedElement(<TopUsers product={SIDER_TABS?.POKER} hideTitle />, {
                    requiredPermission: "poker.reporting.top_users",
                    developmentMode: true,
                    requiredProduct: TRIVELTA_PRODUCTS.POKER,
                    requiredProductFallbackFeature: "poker",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.POKER_CONFIGURATION_CASH_GAME_LIMITS}
                element={
                  protectedElement(
                    <PokerConfigurationPage pageKey="cashGameLimits" />,
                    {
                      requiredPermission:
                        POKER_CONFIGURATION_PAGES.cashGameLimits.permission,
                      developmentMode: true,
                      requiredProduct: TRIVELTA_PRODUCTS.POKER,
                      requiredProductFallbackFeature: "poker",
                    },
                  )
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.POKER_CONFIGURATION_STAKES}
                element={
                  protectedElement(
                    <PokerConfigurationPage pageKey="stakes" />,
                    {
                      requiredPermission:
                        POKER_CONFIGURATION_PAGES.stakes.permission,
                      developmentMode: true,
                      requiredProduct: TRIVELTA_PRODUCTS.POKER,
                      requiredProductFallbackFeature: "poker",
                    },
                  )
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.POKER_CONFIGURATION_PRIVATE_GAMES}
                element={
                  protectedElement(
                    <PokerConfigurationPage pageKey="privateGames" />,
                    {
                      requiredPermission:
                        POKER_CONFIGURATION_PAGES.privateGames.permission,
                      developmentMode: true,
                      requiredProduct: TRIVELTA_PRODUCTS.POKER,
                      requiredProductFallbackFeature: "poker",
                    },
                  )
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.POKER_CONFIGURATION_TOURNAMENTS}
                element={
                  protectedElement(
                    <PokerConfigurationPage pageKey="tournaments" />,
                    {
                      requiredPermission:
                        POKER_CONFIGURATION_PAGES.tournaments.permission,
                      developmentMode: true,
                      requiredProduct: TRIVELTA_PRODUCTS.POKER,
                      requiredProductFallbackFeature: "poker",
                    },
                  )
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.POKER_CONFIGURATION_TIMEBANK}
                element={
                  protectedElement(
                    <PokerConfigurationPage pageKey="timebank" />,
                    {
                      requiredPermission:
                        POKER_CONFIGURATION_PAGES.timebank.permission,
                      developmentMode: true,
                      requiredProduct: TRIVELTA_PRODUCTS.POKER,
                      requiredProductFallbackFeature: "poker",
                    },
                  )
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.POKER_CONFIGURATION_FEATURES}
                element={
                  protectedElement(
                    <PokerConfigurationPage pageKey="features" />,
                    {
                      requiredPermission:
                        POKER_CONFIGURATION_PAGES.features.permission,
                      developmentMode: true,
                      requiredProduct: TRIVELTA_PRODUCTS.POKER,
                      requiredProductFallbackFeature: "poker",
                    },
                  )
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.CASINO_CONFIGURATION_GAMES}
                element={
                  protectedElement(<GamesConfigurationPage />, {
                    requiredPermission: [
                      "casino.configuration.games",
                    ],
                    requiredProduct: TRIVELTA_PRODUCTS.CASINO,
                    requiredProductFallbackFeature: "casino",
                  })
                }
              />

              <Route
                path={NAVIGATION_ROUTES?.CASINO_CONFIGURATION_PROVIDERS}
                element={
                  protectedElement(<ProvidersConfigurationPage />, {
                    requiredPermission: [
                      "casino.configuration.providers",
                    ],
                    requiredProduct: TRIVELTA_PRODUCTS.CASINO,
                    requiredProductFallbackFeature: "casino",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.CASINO_CONFIGURATION_CATALOGS}
                element={
                  protectedElement(<CollectionsConfigurationPage />, {
                    requiredPermission: [
                      "casino.configuration.collections",
                    ],
                    requiredProduct: TRIVELTA_PRODUCTS.CASINO,
                    requiredProductFallbackFeature: "casino",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.CASINO_CONFIGURATION_VIEWS}
                element={
                  protectedElement(<ViewsConfigurationPage />, {
                    requiredPermission: [
                      "casino.configuration.views",
                    ],
                    requiredProduct: TRIVELTA_PRODUCTS.CASINO,
                    requiredProductFallbackFeature: "casino",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.CASINO_CONFIGURATION_TAGS}
                element={
                  protectedElement(<TagsConfigurationPage />, {
                    requiredPermission: [
                      "casino.configuration.tags",
                    ],
                    requiredProduct: TRIVELTA_PRODUCTS.CASINO,
                    requiredProductFallbackFeature: "casino",
                  })
                }
              />
              {/*
                Casino → Configuration → Preview (CASENG-1242). Gated by
                `casino.configuration.preview` from available_permissions.
              */}
              <Route
                path={NAVIGATION_ROUTES?.CASINO_CONFIGURATION_PREVIEW}
                element={
                  protectedElement(<CasinoPreviewLazyRoute />, {
                    requiredPermission: "casino.configuration.preview",
                    requiredProduct: TRIVELTA_PRODUCTS.CASINO,
                    requiredProductFallbackFeature: "casino",
                  })
                }
              />
              {/*
                Casino → Configuration → Pages (CASENG-995).
                Per-client platform-config flag — off by default. Direct URL
                access is blocked when `casino_features.casino_configuration_pages`
                is false for the client.
              */}
              <Route
                path={NAVIGATION_ROUTES?.CASINO_CONFIGURATION_PAGES}
                element={
                  protectedElement(<PagesConfigurationPage />, {
                    requiredPermission: "casino.configuration.pages",
                    featureFlag: "casinoConfigurationPages",
                    requiredProduct: TRIVELTA_PRODUCTS.CASINO,
                    requiredProductFallbackFeature: "casino",
                  })
                }
              />
              {/*
                Casino → Configuration → Features (CASENG-976).
                Gated by `casino.configuration.features` only — same roles as
                Games. Jackpot card stays env-gated inside the page until GA.
              */}
              <Route
                path={NAVIGATION_ROUTES?.CASINO_CONFIGURATION_FEATURES}
                element={
                  protectedElement(<FeaturesConfigurationPage />, {
                    requiredPermission: "casino.configuration.features",
                    requiredProduct: TRIVELTA_PRODUCTS.CASINO,
                    requiredProductFallbackFeature: "casino",
                  })
                }
              />
              {/*
                Predictions → Reports / Configuration. WIP scaffolding:
                `developmentMode: true` keeps the pages reachable only in
                dev/test environments until backend permissions ship.
              */}
              <Route
                path={NAVIGATION_ROUTES?.PREDICTIONS_REPORTS}
                element={
                  protectedElement(<PredictionsReports />, {
                    requiredPermission: "predictions.reports",
                    developmentMode: true,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.PREDICTIONS_CONFIGURATION}
                element={
                  protectedElement(<PredictionsConfiguration />, {
                    requiredPermission: "predictions.configuration",
                    developmentMode: true,
                  })
                }
              />
              {/*
                Configurations → Profile / Feature Quick Access (FE-2349).
                Permission: `configurations.profile` (no `features.quick_access`).
                Also TCM product `core_app` (#1898) and
                `featureFlag: "featureQuickAccess"`.
              */}
              <Route
                path={NAVIGATION_ROUTES?.CONFIGURATIONS_PROFILE}
                element={
                  protectedElement(<ProfileConfigurationPage />, {
                    requiredPermission: "configurations.profile",
                    requiredProduct: TRIVELTA_PRODUCTS.CORE_APP,
                    featureFlag: "featureQuickAccess",
                  })
                }
              />
              {/*
                Configurations → State Configuration (PENG-78). Per-US-state
                experience config (Prediction vs Sweepstakes, signup age,
                per-product toggles). Requires the Predictions product, with
                `developmentMode: true` until the backend permission ships.
              */}
              <Route
                path={NAVIGATION_ROUTES?.CONFIGURATIONS_STATE_CONFIGURATION}
                element={
                  protectedElement(<StateConfigurationPage />, {
                    requiredPermission: "configurations.state_configuration",
                    developmentMode: true,
                    requiredProduct: TRIVELTA_PRODUCTS.PREDICTIONS,
                    requiredProductCatalogOnly: true,
                  })
                }
              />
              {/*
                Configurations → Scheduled Tasks (CASENG-1048).
                Manual trigger + run history for admin_panel_casino scheduled
                tasks. Purposes are casino-only today, so the route keeps the
                casino permission/product gates. The Trigger action inside the
                page is additionally gated by `casino.scheduled_tasks.trigger`.
              */}
              <Route
                path={NAVIGATION_ROUTES?.CONFIGURATIONS_SCHEDULED_TASKS}
                element={
                  protectedElement(<CasinoScheduledTasks />, {
                    requiredPermission: "casino.scheduled_tasks.read",
                    requiredProduct: TRIVELTA_PRODUCTS.CASINO,
                    // Catalog-only to match the Sider entry — a tenant on the
                    // legacy casino flag but absent from the v2 catalog must
                    // not reach the route by URL either.
                    requiredProductCatalogOnly: true,
                  })
                }
              />
              {/*
                Game Pass → Configuration. Permission plus `featureFlag: "gamePass"`
                (`additional_features.game_pass`) blocks direct URLs when the flag
                is off (FE-2025).
              */}
              {/* TODO(PAMENG-1820): drop `requiredProductFallbackFeature`
                  from the Game Pass routes below once BE exposes `gamepass`
                  everywhere. */}
              <Route
                path={NAVIGATION_ROUTES?.GAME_PASS_CONFIGURATION_SEASONS}
                element={
                  protectedElement(<SeasonsList />, {
                    requiredPermission: "game_pass.configuration.seasons",
                    requiredProduct: TRIVELTA_PRODUCTS.GAME_PASS,
                    requiredProductFallbackFeature: "gamePass",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.GAME_PASS_SEASON_NEW}
                element={
                  protectedElement(<CreateSeason />, {
                    requiredPermission: "game_pass.configuration.seasons",
                    requiredProduct: TRIVELTA_PRODUCTS.GAME_PASS,
                    requiredProductFallbackFeature: "gamePass",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.GAME_PASS_SEASON_DETAILS}
                element={
                  protectedElement(<SeasonDetails />, {
                    requiredPermission: "game_pass.configuration.seasons",
                    requiredProduct: TRIVELTA_PRODUCTS.GAME_PASS,
                    requiredProductFallbackFeature: "gamePass",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.GAME_PASS_CONFIGURATION_MONTHLY}
                element={
                  protectedElement(<MonthlyConfiguration />, {
                    requiredPermission: "game_pass.configuration.monthly",
                    requiredProduct: TRIVELTA_PRODUCTS.GAME_PASS,
                    requiredProductFallbackFeature: "gamePass",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.GAME_PASS_MONTHLY_NEW}
                element={
                  protectedElement(<MonthlyChallenge />, {
                    requiredPermission: "game_pass.configuration.monthly",
                    requiredProduct: TRIVELTA_PRODUCTS.GAME_PASS,
                    requiredProductFallbackFeature: "gamePass",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.GAME_PASS_MONTHLY_DETAILS}
                element={
                  protectedElement(<MonthlyDetails />, {
                    requiredPermission: "game_pass.configuration.monthly",
                    requiredProduct: TRIVELTA_PRODUCTS.GAME_PASS,
                    requiredProductFallbackFeature: "gamePass",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.GAME_PASS_CONFIGURATION_STYLE}
                element={
                  protectedElement(<GamePassStyle />, {
                    requiredPermission: "game_pass.configuration.style",
                    developmentMode: true,
                    requiredProduct: TRIVELTA_PRODUCTS.GAME_PASS,
                    requiredProductFallbackFeature: "gamePass",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.SPORTSBOOK_FM}
                element={
                  protectedElement(<SportsbookFM />, {
                    requiredPermission: [
                      "sportsbook.configuration.sportsbook_fm",
                      "configuration.sportsbook_fm",
                    ],
                    requiredProduct: TRIVELTA_PRODUCTS.SPORTSBOOK,
                    requiredProductFallbackFeature: "sportsbook_v2",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.REPORTS}
                element={protectedElement(<Reports />, {
                  requiredPermission: "reports",
                })}
              />
              <Route
                path={NAVIGATION_ROUTES?.PROMOTIONS}
                element={
                  protectedElement(
                    <BonusConfigs />,
                    {
                      requiredPermission: "promotions.bonus_controls",
                      requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                    }
                  )
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.MARKETING_AFFILIATES}
                element={
                  protectedElement(<Affiliates />, {
                    requiredPermission: "promotions.affiliates",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.MARKETING_AFFILIATE_NEW}
                element={
                  protectedElement(<NewAffiliate />, {
                    requiredPermission: "promotions.affiliates",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.MARKETING_AFFILIATE_PROFILE}
                element={
                  protectedElement(<AffiliateProfile />, {
                    requiredPermission: "promotions.affiliates",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.UTM_REPORTS}
                element={
                  protectedElement(<UtmReports />, {
                    requiredPermission: "promotions.utm_reports",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.PROMOTIONS_MANAGEMENT}
                element={
                  protectedElement(<PromotionsManagement />, {
                    requiredPermission: "promotions.bonus_controls",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path="/promotions-management/campaign/:slug"
                element={
                  protectedElement(<CampaignDetails />, {
                    requiredPermission: "promotions.bonus_controls",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path="/promotions-management/new-promotion"
                element={
                  protectedElement(<NewPromotion />, {
                    requiredPermission: "promotions.bonus_controls",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.TRANSACTIONS_WALLET}
                element={
                  protectedElement(
                    <Wallet />,
                    {
                      requiredPermission: "compliance.wallet",
                      requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                    }
                  )
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.TRANSACTIONS_REPORTS}
                element={
                  protectedElement(
                    <TransactionsTable />,
                    {
                      requiredPermission: "payments.reports.report_generator",
                      requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                    }
                  )
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.WITHDRAWAL_REVIEW}
                element={
                  protectedElement(<WithdrawalReview />, {
                    requiredPermission: WITHDRAWAL_REVIEW_ROUTE_PERMISSIONS,
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.TRANSACTIONS_ROUTING}
                element={
                  protectedElement(<Routing />, {
                    requiredPermission: "payments.configuration.routing",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.FRAUD_DETECTION_EDIT_RULE}
                element={
                  protectedElement(<EditRule />, {
                    requiredPermission: "compliance.fraud_detection",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.FRAUD_DETECTION}
                element={
                  protectedElement(<FraudDetection />, {
                    requiredPermission: "compliance.fraud_detection",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              {/*
                The `surt` + `surtCheckpoints` flag gate is inside
                ConfigurationPage (it renders a disabled notice when either is
                off, rather than redirecting — `compliance` resolves to this
                route in REDIRECT_PRIORITY, so a redirect would loop) —
                `featureFlag` here takes a single flag and these two are AND-ed.
              */}
              <Route
                path={NAVIGATION_ROUTES?.CONFIGURATIONS_KYC}
                element={
                  protectedElement(<ConfigurationPage />, {
                    requiredPermission: "compliance",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.DUPLICATE_IDENTITY_REVIEW}
                element={
                  protectedElement(<DuplicateIdentityReview />, {
                    requiredPermission: DUPLICATE_IDENTITY_REVIEW_ROUTE_PERMISSIONS,
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                    featureFlag: "duplicateIdentityReview",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.COMPLIANCE_MAINTENANCE_MODE}
                element={
                  protectedElement(<MaintenanceMode />, {
                    requiredPermission: MAINTENANCE_MODE_ROUTE_PERMISSIONS,
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              {staffRegulatorsAppRoute}
              <Route
                path={NAVIGATION_ROUTES?.EVENT_DETAILS}
                element={
                  protectedElement(<EventDetails />, {
                    requiredPermission: "sportsbook.fieldbook.events_list",
                    requiredProduct: TRIVELTA_PRODUCTS.SPORTSBOOK,
                    requiredProductFallbackFeature: "sportsbook_v2",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.BETS_TYPES_LIST}
                element={
                  protectedElement(<BetsTypesList />, {
                    requiredPermission: "sportsbook.fieldbook.events_list",
                    requiredProduct: TRIVELTA_PRODUCTS.SPORTSBOOK,
                    requiredProductFallbackFeature: "sportsbook_v2",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.SGP_RISK}
                element={
                  protectedElement(<SGPRisk />, {
                    requiredPermission: "sportsbook.monitoring.sgp_risk",
                    requiredProduct: TRIVELTA_PRODUCTS.SPORTSBOOK,
                    requiredProductFallbackFeature: "sportsbook_v2",
                  })
                }
              />
              <Route
                path={SGP_RISK_BETS_LIST_ROUTE}
                element={
                  protectedElement(<SGPIdBetsList />, {
                    requiredPermission: "sportsbook.monitoring.sgp_risk",
                    requiredProduct: TRIVELTA_PRODUCTS.SPORTSBOOK,
                    requiredProductFallbackFeature: "sportsbook_v2",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.SPORTSBOOK_LIMITS}
                element={
                  protectedElement(<SGPLimits />, {
                    requiredPermission: "sportsbook.configuration.sgp_limits",
                    requiredProduct: TRIVELTA_PRODUCTS.SPORTSBOOK,
                    requiredProductFallbackFeature: "sportsbook_v2",
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.DYNAMIC_BONUS_TEMPLATES}
                element={
                  protectedElement(<TemplatesPage />, {
                    requiredPermission: "promotions.templates",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.DYNAMIC_BONUS_TEMPLATE_NEW}
                element={
                  protectedElement(<CreateTemplatePage />, {
                    requiredPermission: "promotions.templates",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.DYNAMIC_BONUS_TEMPLATE_EDIT}
                element={
                  protectedElement(<EditTemplatePage />, {
                    requiredPermission: "promotions.templates",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.DYNAMIC_BONUS_SEGMENTS}
                element={
                  protectedElement(<SegmentsPage />, {
                    requiredPermission: "promotions.segments",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.DYNAMIC_BONUS_SEGMENT_NEW}
                element={
                  protectedElement(<CreateSegmentPage />, {
                    requiredPermission: "promotions.segments",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.DYNAMIC_BONUS_SEGMENT_DETAIL}
                element={
                  protectedElement(<SegmentDetailPage />, {
                    requiredPermission: "promotions.segments",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.DYNAMIC_BONUS_CAMPAIGNS}
                element={
                  protectedElement(<CampaignsPage />, {
                    requiredPermission: "promotions.campaigns",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.DYNAMIC_BONUS_CAMPAIGN_NEW}
                element={
                  protectedElement(<CreateCampaignPage />, {
                    requiredPermission: "promotions.campaigns",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              {/* `new-affiliate` is a static path declared before the dynamic
                  `:campaignId` route below. React Router v6 ranks static
                  segments above dynamic ones, but keeping this order makes the
                  precedence explicit. */}
              <Route
                path={NAVIGATION_ROUTES?.DYNAMIC_BONUS_CAMPAIGN_NEW_AFFILIATE}
                element={
                  protectedElement(<CreateAffiliateCampaignPage />, {
                    requiredPermission: "promotions.campaigns",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.DYNAMIC_BONUS_CAMPAIGN_DETAIL}
                element={
                  protectedElement(<EditCampaignPage />, {
                    requiredPermission: "promotions.campaigns",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              {/* Series edit before detail — `/edit` suffix must not be
                  captured as a seriesId. Series are created from the Campaigns
                  wizard and listed in the Campaigns table. */}
              <Route
                path={NAVIGATION_ROUTES?.DYNAMIC_BONUS_SERIES_EDIT}
                element={
                  protectedElement(<EditSeriesPage />, {
                    requiredPermission: "promotions.campaigns",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.DYNAMIC_BONUS_SERIES_DETAIL}
                element={
                  protectedElement(<SeriesDetailPage />, {
                    requiredPermission: "promotions.campaigns",
                    requiredProduct: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
                  })
                }
              />
              </Route>
              <Route path="*" element={<Navigate to={NAVIGATION_ROUTES?.LOGIN} replace />} />
            </Routes>
          </>
        </AuthProvider>
      )}
    </div>
  );
};

export default App;
